import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import ListView from "../../../utils/Custom/listView";
import ListSearchSection from "../../../utils/Custom/ListSearchSection";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { submitSiteDelete } from "../../../utils/commonComponent/delete";
import { AddButton } from "../../../utils/Custom/Button";
import { userTypes } from "../../constants/userConstant";
import { getPermissions } from "../rbac/hasAccess";

const headers = ["id","employeeName", "userName",  "type"];

const fields = [
  { value: "employeeName", label: "Employee Name" },
  { value: "username", label: "Username" },
];

const getTypeLabel = (typeId) => {
  const found = userTypes.find((t) => t.id == typeId);
  return found ? found.label : "";
};

const UserMasterList = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = getPermissions(props.moduleId);
  const handleEdit = (row) => navigate(`/master/users/${row.id}`);
  const handleDelete = (row) => submitSiteDelete("users", row.id);

  const [userList, setUserList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [allUsersData, setAllUsersData] = useState([]);
  const [searchBy, setSearchBy] = React.useState(sessionStorage.getItem("searchBy") || "username");
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  const [searched, setSearched] = React.useState(sessionStorage.getItem("searched") === "true" || false);
  // Pagination
  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    // fetch new data from backend here
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const getWhereClause = () => {
    let whereConditions = "";
    const colMap = {
      employeeName: "e.name",
      username: "users.userName",
    };
    const colName = colMap[searchBy];
    if (colName && searchKey.trim()) {
      whereConditions = ` AND LOWER(${colName}) LIKE LOWER('%${searchKey.trim()}%')`;
    }

    return whereConditions;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause();
    let paginationSettings = "";
    if (rowsPerPage !== -1) {
      paginationSettings = `LIMIT ${rowsPerPage} OFFSET ${rowsPerPage * Number(currentPage)}`;
    }
    return `SELECT users.*, e.name AS employeeName FROM users LEFT JOIN employees e ON e.id = users.employeeId WHERE users.status = 1 ${whereClause} ORDER BY id DESC ${paginationSettings};`;
  };

  const getCountQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT COUNT(*) AS totalRows FROM users LEFT JOIN employees e ON e.id = users.employeeId WHERE users.status = 1 ${whereClause};`;
  };

  const fetchData = async () => {
    try {
      const allDataQuery = fetchAllUsersData();
      const [userList, countResult, allUsersData] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
        fetchQuery(allDataQuery),
      ]);
      // Map numeric type to label
      const formattedData = userList.map((user) => ({
        ...user,
        type: getTypeLabel(user.type), // Replacing `type` number with label
      }));

      const formattedAllUsersData = allUsersData.map((user) => ({
        ...user,
        type: getTypeLabel(user.type),
      }));
      
      setUserList(formattedData);
      setAllUsersData(formattedAllUsersData);
      setTotalRows(countResult?.[0]?.totalRows || 0);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleSearch = () => {
    if (!searchBy || !searchKey.trim()) {
      alert("Please select a search field and enter a search value.");
      return;
    }
    fetchData();
    setSearched(true);
    // Save search state to sessionStorage
    sessionStorage.setItem("searchBy", searchBy);
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched", "true");
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearched(false);
    setSearchBy("username");
    setSearchKey("");
    // Clear all employee list session storage
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    sessionStorage.removeItem("searchBy");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    setCurrentPage(0);
  };

  const fetchAllUsersData = () => {
    const whereClause = getWhereClause();
    
    return `SELECT users.*, e.name AS employeeName FROM users LEFT JOIN employees e ON e.id = users.employeeId WHERE users.status = 1 ${whereClause} ORDER BY id DESC`; };

  useEffect(() => {
    fetchData();
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    if (searched == false) {
      fetchData();
    }
  }, [searched]);

  return (
    <>
      {(permissions.create || permissions.allAccess) && (
        <AddButton
          id="btn-add-outline"
          to="/master/users/0"
          label="Add Users"
          icon={<AddIcon fontSize="small" />}
        />
      )}

      <ListSearchSection
        title="Search Users"
        fields={fields}
        onSearch={handleSearch}
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        searchBy={searchBy}
        setSearchBy={setSearchBy}
        clearSearch={clearSearch}
        searched={searched}
        setSearched={setSearched}
        showExcelDownload={permissions.excelDownload || permissions.allAccess}
        finalData={allUsersData}
        notNeededColumn={["status", "employeeId", "userStatus", "roles","usersign","policyDate","policyStatus","groupId", "disciplineId", "emailId", "contactNo", "editAccess", "modules", "id"]}
      />

      <ListView
        headers={headers}
        data={userList}
        actions={{
          edit: handleEdit,
          delete: handleDelete,
        }}
        moduleId={props.moduleId}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPaginationChange={handlePaginationChange}
      />
    </>
  );
};

export default UserMasterList;
